/**
 * Delicious / OishiE Music Player – Theme Blocks Edition
 * ・トラック / スライドを DOM（theme blocks）から読み取る方式に変更
 * ・スライド切替を BPM（曲のテンポ）に同期
 * ・デザインテキストの「バラバラ→集合」アニメーション
 * ・画像 / テキストスライドのタップで指定リンクへ遷移
 * ・複数インスタンス対応（全DOMクエリをコンテナスコープに限定）
 */
(function () {
  'use strict';

  const players = new Map(); // container -> api
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* =========================================
     1つのプレイヤーコンテナを初期化
     ========================================= */
  function initPlayer(container) {
    /* --- DOM（すべて container スコープ） --- */
    const dataRoot     = container.querySelector('.dm-tracks-data');
    const controls     = container.querySelector('.dm-playlist-controls');
    const nowPlayingEl = container.querySelector('.dm-now-playing');
    const stopBtn      = container.querySelector('.dm-stop-button');
    const seekBar      = container.querySelector('.dm-seek-bar');
    const helpTextEl   = container.querySelector('.dm-help-text');
    const visual       = container.querySelector('.dm-visual');
    const visualImage  = container.querySelector('.dm-visual-image');
    const textStage    = container.querySelector('.dm-text-stage');

    if (!dataRoot || !controls) return;

    const selectLabel = nowPlayingEl ? nowPlayingEl.textContent.trim() : 'Select a track';
    const defaultImageSrc = visualImage ? visualImage.getAttribute('src') : '';

    /* --- 状態管理（このプレイヤー専用） --- */
    const audio = new Audio();
    audio.loop = true;
    audio.preload = 'auto';

    let tracks            = [];
    let currentTrackUrl   = null;
    let currentSlides     = [];
    let currentSlideIndex = 0;
    let currentInterval   = 6000;
    let currentLink       = '';
    let isDragging        = false;
    let slideTimer        = null;
    let bgTimer           = null;   // エンドロール中の背景画像巡回タイマー
    let bgImages          = [];     // 背景に使う画像スライドのURL一覧
    let bgIndex           = 0;
    let isEndrollActive   = false;  // 現在エンドロール表示中か
    let cyclingEnabled    = false;  // 再生中（自動送り有効）か

    /* --- トラックデータを DOM から収集 --- */
    function collectTracks() {
      tracks = Array.from(dataRoot.querySelectorAll('.dm-track-data')).map((trackEl) => {
        const iconEl = trackEl.querySelector('.dm-track-icon-src');
        const slides = Array.from(trackEl.querySelectorAll('.dm-slide')).map((el) => ({
          el,
          type:      el.dataset.type || 'image',
          image:     el.dataset.image || '',
          link:      el.dataset.link || '',
          animation: el.dataset.animation || 'gather',
          color:     el.dataset.color || '',
          size:      el.dataset.size || 'm',
          duration:  parseFloat(el.dataset.duration) || 2,
          rollSpeed: el.dataset.rollSpeed || 'normal',
          lineHeight: el.dataset.lineHeight || '',
          letterSpacing: el.dataset.letterSpacing || '',
          bg:        el.dataset.bg || '',
          text:      el.dataset.type === 'text'
            ? (el.querySelector('.dm-slide-text-source')?.textContent || '')
            : ''
        }));

        return {
          el: trackEl,
          id:     trackEl.dataset.blockId,
          title:  trackEl.dataset.title || '',
          audio:  trackEl.dataset.audio || '',
          status: trackEl.dataset.status || 'ready',
          bpm:    parseFloat(trackEl.dataset.bpm) || 0,
          beats:  parseFloat(trackEl.dataset.beats) || 0,
          icon:   iconEl ? iconEl.innerHTML.trim() : '',
          slides
        };
      });
    }

    function computeInterval(track) {
      if (track.bpm > 0 && track.beats > 0) {
        return (60000 / track.bpm) * track.beats;
      }
      return 6000; // フォールバック
    }

    /* =========================================
       プレイリスト（トラックボタン）構築
       ========================================= */
    function buildControls() {
      controls.innerHTML = '';

      if (tracks.length === 0) {
        if (nowPlayingEl) nowPlayingEl.textContent = 'No tracks available. Add tracks in Theme Editor.';
        return;
      }

      tracks.forEach((track) => {
        const btn = document.createElement('button');
        btn.className = 'dm-track-btn';
        btn.type = 'button';
        btn.dataset.blockId = track.id;
        btn.innerHTML = `
          <div class="dm-track-icon">${track.icon}</div>
          <span class="dm-track-title">${escapeHTML(track.title)}</span>
        `;

        if (track.status === 'coming') {
          btn.classList.add('is-coming');
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.innerHTML += `<span class="dm-status-label">Coming Soon</span>`;
        } else {
          btn.addEventListener('click', () => {
            if (!track.audio) {
              if (nowPlayingEl) nowPlayingEl.textContent = 'Error: Audio file not found.';
              return;
            }
            if (currentTrackUrl === track.audio && !audio.paused) {
              stopTrack();
              return;
            }
            playTrack(track);
          });
        }

        controls.appendChild(btn);
      });
    }

    /* =========================================
       再生・停止
       ========================================= */
    function playTrack(track) {
      if (currentTrackUrl === track.audio && !audio.paused) return;

      currentTrackUrl = track.audio;
      audio.src = track.audio;
      audio.load();

      const p = audio.play();
      if (p !== undefined) p.catch((e) => console.error('DM: Playback error:', e));

      updateUI(track, true);
    }

    function stopTrack() {
      audio.pause();
      audio.currentTime = 0;
      currentTrackUrl = null;

      stopCycle();
      stopBgCycle();
      isEndrollActive = false;
      clearText();
      resetToDefault();

      if (nowPlayingEl) nowPlayingEl.textContent = selectLabel;
      if (seekBar) {
        seekBar.value = 0;
        seekBar.disabled = true;
        seekBar.style.backgroundSize = '0% 100%';
      }
      container.querySelectorAll('.dm-track-btn').forEach((b) => b.classList.remove('active'));
    }

    /* =========================================
       UI更新（トラック切替時）
       ========================================= */
    function updateUI(track, autoPlay) {
      if (nowPlayingEl) nowPlayingEl.textContent = `Now Playing: ${track.title}`;
      if (helpTextEl) helpTextEl.style.display = 'none';

      container.querySelectorAll('.dm-track-btn').forEach((b) => b.classList.remove('active'));
      const activeBtn = container.querySelector(`.dm-track-btn[data-block-id="${track.id}"]`);
      if (activeBtn) activeBtn.classList.add('active');

      currentSlides = track.slides;
      currentSlideIndex = 0;
      currentInterval = computeInterval(track);
      cyclingEnabled = autoPlay;

      stopCycle();
      stopBgCycle();
      clearText();

      if (currentSlides.length > 0) {
        showSlide(0);
        // エンドロール中はテキストを保持するため、メインの送りは開始しない
        if (currentSlides.length > 1 && autoPlay && !isEndrollActive) startCycle();
      } else {
        resetToDefault();
      }
    }

    /* =========================================
       スライド切替（BPM同期）
       ========================================= */
    function startCycle() {
      stopCycle();
      if (currentSlides.length <= 1) return;
      slideTimer = setInterval(() => {
        showSlide(currentSlideIndex + 1);
      }, currentInterval);
    }

    function stopCycle() {
      if (slideTimer) {
        clearInterval(slideTimer);
        slideTimer = null;
      }
    }

    /* =========================================
       エンドロール中の背景画像巡回
       ・トラック内の画像スライドを、設定タイミング（BPM同期）で背景として切替
       ========================================= */
    function startBgCycle(rollSlide) {
      stopBgCycle();

      bgImages = currentSlides
        .filter((s) => s.type === 'image' && s.image)
        .map((s) => s.image);

      // 画像スライドが無ければ、ロール自身の背景画像（あれば）を表示
      if (!bgImages.length) {
        if (rollSlide && rollSlide.bg) changeImage(rollSlide.bg);
        return;
      }

      bgIndex = 0;
      changeImage(bgImages[0]);

      // 再生中のみ巡回（テーマエディタのプレビューでは先頭画像で静止）
      if (cyclingEnabled && bgImages.length > 1) {
        bgTimer = setInterval(() => {
          bgIndex = (bgIndex + 1) % bgImages.length;
          changeImage(bgImages[bgIndex]);
        }, currentInterval);
      }
    }

    function stopBgCycle() {
      if (bgTimer) {
        clearInterval(bgTimer);
        bgTimer = null;
      }
    }

    function showSlide(i) {
      if (!currentSlides.length) return;
      const len = currentSlides.length;
      currentSlideIndex = ((i % len) + len) % len;
      const slide = currentSlides[currentSlideIndex];

      currentLink = slide.link || '';
      if (visual) visual.classList.toggle('has-link', !!currentLink);

      isEndrollActive = false;
      stopBgCycle();

      if (slide.type === 'text') {
        // エンドロール：テキストを常時オーバーレイし、背景の画像スライドを巡回させる
        if (slide.animation === 'roll') {
          isEndrollActive = true;
          stopCycle();        // メインの送りを止めてロール表示を保持
          renderText(slide);
          startBgCycle(slide);
          return;
        }
        // 背景画像が指定されていれば差し替え（is-text-activeで自動的に暗くなる）
        if (slide.bg) changeImage(slide.bg);
        renderText(slide);
      } else {
        clearText();
        if (slide.image) changeImage(slide.image);
      }
    }

    /* =========================================
       デザインテキスト（バラバラ→集合）
       ========================================= */
    function renderText(slide) {
      if (!textStage) return;
      const anim = slide.animation || 'gather';

      // エンドロール（長文向け）は専用描画へ
      if (anim === 'roll') {
        renderTextRoll(slide);
        return;
      }

      textStage.innerHTML = '';
      textStage.className = 'dm-text-stage';
      if (visual) visual.classList.remove('is-scroll-hint');
      textStage.classList.add('anim-' + anim, 'size-' + (slide.size || 'm'));
      if (slide.color) textStage.style.setProperty('--dm-text-color', slide.color);

      // 演出の長さ（1〜3秒）・行間・カーニング
      const dur = Math.min(3, Math.max(1, slide.duration || 2));
      textStage.style.setProperty('--dm-gather-dur', dur + 's');
      if (slide.lineHeight) textStage.style.setProperty('--dm-lh', slide.lineHeight);
      if (slide.letterSpacing !== '') textStage.style.setProperty('--dm-ls', slide.letterSpacing + 'px');

      const lines = slide.text.split('\n');
      lines.forEach((lineText) => {
        const lineEl = document.createElement('div');
        lineEl.className = 'dm-text-line';

        Array.from(lineText).forEach((ch) => {
          const span = document.createElement('span');
          span.className = 'dm-char';

          if (ch === ' ' || ch === '　') {
            span.classList.add('dm-char--space');
            span.innerHTML = '&nbsp;';
          } else {
            const inner = document.createElement('span');
            inner.className = 'dm-char-inner';
            inner.textContent = ch;
            span.appendChild(inner);
          }

          const sc = scatterFor(anim);
          span.style.setProperty('--x', sc.x);
          span.style.setProperty('--y', sc.y);
          span.style.setProperty('--r', sc.r);
          span.style.setProperty('--s', sc.s);
          span.style.setProperty('--o', sc.o);
          span.style.setProperty('--b', sc.b);
          span.style.setProperty('--d', sc.d);

          lineEl.appendChild(span);
        });

        textStage.appendChild(lineEl);
      });

      if (visual) visual.classList.add('is-text-active');
      textStage.classList.add('is-active');

      if (reduceMotion) {
        textStage.classList.add('is-visible', 'is-gathered');
        return;
      }

      requestAnimationFrame(() => {
        textStage.classList.add('is-visible');
        // 浮遊状態を一瞬見せてから「スッ」と集合（合計 ~1.5秒以内）
        setTimeout(() => textStage.classList.add('is-gathered'), 320);
      });
    }

    /* =========================================
       エンドロール（長文向け・下から上へ流れる）
       ・行単位で描画（1文字spanを作らないので長文でも軽量）
       ・文章量を実測し、px/秒を一定に保って読む速さをそろえる
       ========================================= */
    function renderTextRoll(slide) {
      if (!textStage) return;

      textStage.innerHTML = '';
      textStage.className = 'dm-text-stage anim-roll size-' + (slide.size || 's');
      if (slide.color) textStage.style.setProperty('--dm-text-color', slide.color);
      if (slide.lineHeight) textStage.style.setProperty('--dm-lh', slide.lineHeight);
      if (slide.letterSpacing !== '') textStage.style.setProperty('--dm-ls', slide.letterSpacing + 'px');

      const roll = document.createElement('div');
      roll.className = 'dm-text-roll';
      slide.text.split('\n').forEach((lineText) => {
        const lineEl = document.createElement('div');
        lineEl.className = 'dm-text-line';
        // 空行も高さを保つ
        lineEl.textContent = lineText === '' ? ' ' : lineText;
        roll.appendChild(lineEl);
      });
      textStage.appendChild(roll);

      if (visual) visual.classList.add('is-text-active');
      textStage.classList.add('is-active', 'is-visible');

      // 視差軽減：自動で流さず、指で読み進められるようにする
      // （OSの「視差効果を減らす」を尊重しつつ、長文を最後まで読めるようにする）
      if (reduceMotion) {
        roll.style.animation = 'none';
        roll.style.transform = 'translateY(0)';
        textStage.classList.add('is-scrollable');
        if (visual) visual.classList.add('is-scroll-hint');
        return;
      }

      // レイアウト実測（移動距離 = ステージ高さ + 文章高さ）
      const stageH = textStage.clientHeight || 0;
      const rollH  = roll.scrollHeight || 0;
      const travel = stageH + rollH;

      // px/秒（速度）を一定にし、長文ほど時間を長くして読みやすさをそろえる
      const ppsMap = { slow: 35, normal: 60, fast: 95 };
      const pps = ppsMap[slide.rollSpeed] || ppsMap.normal;
      const dur = Math.min(180, Math.max(6, travel / pps));

      roll.style.setProperty('--dm-roll-from', stageH + 'px');
      roll.style.setProperty('--dm-roll-to', (-rollH) + 'px');
      roll.style.setProperty('--dm-roll-dur', dur.toFixed(1) + 's');
    }

    // アニメ種類ごとの初期（散らばり）状態を返す
    // o=透明度, b=ぼかし, s=大きさ をバラバラにしてダイナミックに
    function scatterFor(anim) {
      const rndDelay = (Math.random() * 0.4).toFixed(2) + 's';
      const rndOpacity = (0.15 + Math.random() * 0.5).toFixed(2);
      if (anim === 'rise') {
        return {
          x: '0px', y: (50 + Math.random() * 70).toFixed(0) + 'px', r: '0deg',
          s: (0.85 + Math.random() * 0.3).toFixed(2), o: rndOpacity, b: (Math.random() * 4).toFixed(1) + 'px', d: rndDelay
        };
      }
      if (anim === 'fade') {
        return {
          x: '0px', y: ((Math.random() * 2 - 1) * 20).toFixed(0) + 'px', r: '0deg',
          s: (0.9 + Math.random() * 0.2).toFixed(2), o: rndOpacity, b: (2 + Math.random() * 4).toFixed(1) + 'px', d: rndDelay
        };
      }
      // gather（既定）：位置・回転・大きさ・透明度・ぼかしを大きくバラバラに
      return {
        x: ((Math.random() * 2 - 1) * 200).toFixed(0) + 'px',
        y: ((Math.random() * 2 - 1) * 160).toFixed(0) + 'px',
        r: ((Math.random() * 2 - 1) * 120).toFixed(0) + 'deg',
        s: (0.3 + Math.random() * 1.5).toFixed(2),
        o: rndOpacity,
        b: (2 + Math.random() * 6).toFixed(1) + 'px',
        d: rndDelay
      };
    }

    function clearText() {
      if (!textStage) return;
      textStage.classList.remove('is-active', 'is-visible', 'is-gathered', 'is-scrollable');
      textStage.innerHTML = '';
      if (visual) visual.classList.remove('is-text-active', 'is-scroll-hint');
    }

    /* =========================================
       画像表示（クロスフェード）
       ========================================= */
    function changeImage(newUrl) {
      if (!visualImage || !newUrl) return;

      const currentSrc = visualImage.getAttribute('src') || '';
      if (currentSrc.replace(/^https?:/, '') === newUrl.replace(/^https?:/, '')) {
        visualImage.style.opacity = '1';
        return;
      }

      // フェードアウト中に裏で先読みし、切替時にはキャッシュ済みにする
      const pre = new Image();
      pre.src = newUrl;

      visualImage.style.opacity = '0';
      setTimeout(() => {
        if (visualImage.hasAttribute('srcset')) visualImage.removeAttribute('srcset');
        visualImage.classList.remove('dm-zoom');
        visualImage.src = newUrl;

        const onReady = () => {
          void visualImage.offsetWidth; // reflow でアニメ再起動
          visualImage.classList.add('dm-zoom');
          visualImage.style.opacity = '1';
        };
        if (visualImage.complete && visualImage.naturalWidth > 0) onReady();
        else visualImage.onload = onReady;
      }, 700);
    }

    function resetToDefault() {
      if (!visualImage) return;
      visualImage.classList.remove('dm-zoom');
      if (defaultImageSrc) changeImage(defaultImageSrc);
    }

    /* =========================================
       リンク遷移（ビジュアルのタップ）
       ========================================= */
    if (visual) {
      visual.addEventListener('click', () => {
        if (currentLink) window.location.href = currentLink;
      });
    }

    /* =========================================
       オーディオ・シークバー
       ========================================= */
    function setupAudioListeners() {
      audio.addEventListener('timeupdate', () => {
        if (seekBar && !isDragging && audio.duration) {
          const progress = (audio.currentTime / audio.duration) * 100;
          seekBar.value = progress;
          seekBar.disabled = false;
          seekBar.style.backgroundSize = `${progress}% 100%`;
        }
      });
      if (!audio.loop) audio.addEventListener('ended', stopTrack);
    }

    function setupSeekBar() {
      if (!seekBar) return;
      seekBar.addEventListener('mousedown', () => { isDragging = true; });
      seekBar.addEventListener('touchstart', () => { isDragging = true; }, { passive: true });
      seekBar.addEventListener('change', () => {
        if (audio.duration) audio.currentTime = (seekBar.value / 100) * audio.duration;
        isDragging = false;
      });
      seekBar.addEventListener('input', () => {
        isDragging = true;
        seekBar.style.backgroundSize = `${seekBar.value}% 100%`;
      });
    }

    /* =========================================
       初期化
       ========================================= */
    collectTracks();
    buildControls();

    // 最初に表示されやすい画像を1枚先読みして初回再生を速くする
    for (const t of tracks) {
      const first = t.slides.find((s) => (s.type === 'image' && s.image) || (s.type === 'text' && s.bg));
      if (first) { new Image().src = first.image || first.bg; break; }
    }

    if (stopBtn) stopBtn.addEventListener('click', stopTrack);
    setupSeekBar();
    setupAudioListeners();

    /* --- テーマエディタ用プレビュー / 後片付け API --- */
    function previewBlock(blockId, blockEl) {
      // トラック自体が選択された場合
      let track = tracks.find((t) => t.id === blockId);
      // スライドが選択された場合は親トラックを特定
      if (!track && blockEl) {
        const trackEl = blockEl.closest('.dm-track-data');
        if (trackEl) track = tracks.find((t) => t.el === trackEl);
      }
      if (!track) return;

      if (!audio.paused) stopTrack();
      updateUI(track, false);

      // 選択されたスライドをプレビュー表示
      if (blockEl && blockEl.classList.contains('dm-slide')) {
        const idx = track.slides.findIndex((s) => s.el === blockEl);
        if (idx >= 0) showSlide(idx);
      }
    }

    function teardown() {
      stopCycle();
      stopBgCycle();
      audio.pause();
      audio.src = '';
    }

    players.set(container, { previewBlock, teardown });
  }

  /* =========================================
     ページ内の全プレイヤーを初期化
     ========================================= */
  function initAllPlayers(root) {
    (root || document).querySelectorAll('.dm-container').forEach((c) => {
      if (!players.has(c)) initPlayer(c);
    });
  }

  function escapeHTML(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  /* --- テーマエディタ イベント --- */
  document.addEventListener('shopify:section:load', (e) => {
    initAllPlayers(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('.dm-container').forEach((c) => {
      const api = players.get(c);
      if (api) { api.teardown(); players.delete(c); }
    });
  });

  document.addEventListener('shopify:block:select', (e) => {
    const blockEl = e.target;
    const container = blockEl && blockEl.closest ? blockEl.closest('.dm-container') : null;
    if (!container) return;
    const api = players.get(container);
    if (api && api.previewBlock) api.previewBlock(e.detail.blockId, blockEl);
  });

  /* --- 起動 --- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllPlayers());
  } else {
    initAllPlayers();
  }
})();
